import { useEffect, useRef, useState } from 'react';
import AcButton from '@/dashboard/ui/AcButton';

/**
 * EvidenceCapture -- THE ONE shared photo-capture spine
 * (DOCTOR-CLINICAL-EVIDENCE-TIER2-V1; the PROOF-EVIDENCE-SPINE chartered by
 * the intake brief s.16.1: ONE capture component for reception consent /
 * doctor case-sheet photos / accounts vouchers -- never per-surface forks).
 *
 * EXTRACTION PROVENANCE (readiness report #416 reuse-map rows 2-3): the
 * capture control behaviour is the accounts voucher control's proven shape
 * (hidden `<input type="file" accept capture="environment">` behind a
 * Take-photo/Retake button pair) GENERALIZED: the original bytes are kept
 * FULL QUALITY (clinical truth is never recompressed -- the money control's
 * downscale-as-the-image behaviour is deliberately NOT inherited) and a
 * separate compressed JPEG delivery RENDITION is produced for lists (the
 * D2 compressed-at-rest + full-on-tap posture). Registered in the Product
 * OS component registry (component.evidence-capture).
 *
 * CONVERGENCE PLAN (disclosed, bounded): the intake consent-photo slice
 * (frontend/src/dashboard/patients/consent-photo.tsx `ConsentPhotoCaptureSeam`)
 * and the accounts voucher control are the two existing capture call sites.
 * NEITHER is edited by this package (the front-office lane owns intake
 * presentation and money surfaces in a parallel lane); the intake repoint
 * onto THIS component is a named post-merge follow-up in the Tier-2 run
 * record. The `subject` prop is a superset of the seam's subject vocabulary
 * so the repoint is a wrapper, not a rewrite.
 *
 * The component performs NO network call and stores NOTHING -- it yields the
 * captured payload to the caller (`onCaptured`), and durable storage (with
 * its server-side magic-byte / size / type fences) stays the caller's
 * governed write path. Client-side checks here exist only to anchor an
 * error beside the field BEFORE a doomed upload.
 */

/** Rendition geometry: legible-list scale, never clinical resolution. */
export const EVIDENCE_RENDITION_MAX_EDGE_PX = 800;
const RENDITION_QUALITY_STEPS = [0.72, 0.5] as const;

export interface CapturedEvidencePhoto {
  /** Local picked filename only; not a replacement for saved clinical metadata. */
  readonly fileName?: string;
  /** The FULL-QUALITY original, base64 (exact picked bytes -- never
   *  recompressed client-side). */
  readonly base64: string;
  readonly mimeType: string;
  readonly byteSize: number;
  /** Compressed JPEG delivery rendition, or null when the canvas path is
   *  unavailable or the rendition cannot fit its cap (lists then render
   *  the kind label -- never a broken image). */
  readonly renditionBase64: string | null;
  readonly renditionByteSize: number | null;
  /** Local preview source (the rendition when present, else the original). */
  readonly previewDataUrl: string;
}

function dataUrlToBase64(dataUrl: string): string {
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

function base64ByteSize(base64: string): number {
  return Math.floor((base64.length * 3) / 4);
}

/**
 * Read one picked file into the capture payload: the original base64
 * verbatim + a canvas-downscaled JPEG rendition. Throws Error with a
 * user-anchored message on a type/size violation (mirroring the server
 * fence copy so the beside-the-field error matches the refusal).
 */
export async function fileToCapturedEvidence(
  file: File,
  opts: {
    readonly maxBytes: number;
    readonly allowedTypes: readonly string[];
    readonly renditionMaxBytes: number;
  },
): Promise<CapturedEvidencePhoto> {
  if (!opts.allowedTypes.includes(file.type)) {
    throw new Error('File type not accepted. Choose a supported file.');
  }
  if (file.size > opts.maxBytes) throw new Error(`File is too large. Max ${Math.floor(opts.maxBytes / (1024 * 1024))} MB.`);
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
  const base64 = dataUrlToBase64(dataUrl);
  const byteSize = file.size;
  if (byteSize > opts.maxBytes) {
    throw new Error(
      `Photo is too large. Max ${Math.floor(opts.maxBytes / (1024 * 1024))} MB.`,
    );
  }

  // Documents and videos retain original bytes; never decode them as an image or send them through canvas.
  if (file.type === 'application/pdf' || file.type.startsWith('video/')) return { base64, mimeType: file.type, byteSize, fileName: file.name,
    renditionBase64: null, renditionByteSize: null, previewDataUrl: dataUrl };

  // The rendition: best-effort canvas downscale; null when unavailable.
  const rendition: { base64: string; dataUrl: string } | null =
    await new Promise((resolve) => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        try {
          const scale = Math.min(
            1,
            EVIDENCE_RENDITION_MAX_EDGE_PX / Math.max(img.width, img.height),
          );
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          for (const quality of RENDITION_QUALITY_STEPS) {
            const jpeg = canvas.toDataURL('image/jpeg', quality);
            const jpegBase64 = dataUrlToBase64(jpeg);
            if (base64ByteSize(jpegBase64) <= opts.renditionMaxBytes) {
              resolve({ base64: jpegBase64, dataUrl: jpeg });
              return;
            }
          }
          resolve(null);
        } catch {
          resolve(null);
        }
      };
      img.src = dataUrl;
    });

  return {
    base64,
    mimeType: file.type,
    byteSize,
    renditionBase64: rendition?.base64 ?? null,
    renditionByteSize:
      rendition === null ? null : base64ByteSize(rendition.base64),
    previewDataUrl: rendition?.dataUrl ?? dataUrl,
  };
}

export interface EvidenceCaptureControlProps {
  /** What the photo proves -- a superset of the intake seam's subject
   *  vocabulary (registration_consent / intake_consent / the Tier-2
   *  evidence classes). Rides data-subject for tests; never rendered. */
  readonly subject: string;
  /** Snapshot application target synchronously at file selection, before decoding. */
  readonly onFileSelected?: () => void;
  /** Org-configured capture sources ('camera' / 'upload'). With camera
   *  enabled the input carries capture="environment" (rear camera on
   *  phones; desktops still get the file picker from the same input). */
  readonly captureSources: readonly string[];
  readonly maxBytes: number;
  readonly allowedTypes: readonly string[];
  readonly renditionMaxBytes: number;
  readonly busy?: boolean;
  /** Button label; the pinned intake vocabulary default. */
  readonly label?: string;
  readonly variant?: 'primary' | 'secondary';
  readonly onCaptured: (photo: CapturedEvidencePhoto) => void;
  readonly onError: (message: string) => void;
  readonly onReadStateChange?: (reading: boolean) => void;
  readonly testId?: string;
}

/** The ONE capture control: a visually hidden file input driven by a real
 *  button (the accounts pattern, promoted). */
export function EvidenceCaptureControl(
  props: EvidenceCaptureControlProps,
): JSX.Element {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const generation = useRef(0);
  const mounted = useRef(true);
  const [reading, setReading] = useState(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; generation.current++; }; }, []);
  const cameraEnabled = props.captureSources.includes('camera');
  const accept =
    props.allowedTypes.length > 0 ? props.allowedTypes.join(',') : 'image/*';

  const onPick = async (file: File | null) => {
    if (!file || props.busy) return;
    const request = ++generation.current;
    setReading(true);
    props.onReadStateChange?.(true);
    try {
      const captured = await fileToCapturedEvidence(file, {
        maxBytes: props.maxBytes,
        allowedTypes: props.allowedTypes,
        renditionMaxBytes: props.renditionMaxBytes,
      });
      if (mounted.current && request === generation.current) {
        props.onReadStateChange?.(false);
        props.onCaptured(captured);
      }
    } catch (err) {
      if (mounted.current && request === generation.current) props.onError(err instanceof Error ? err.message : 'File could not be read');
    } finally { if (mounted.current && request === generation.current) { setReading(false); props.onReadStateChange?.(false); } }
  };

  return (
    <span data-subject={props.subject}>
      <AcButton
        size="sm"
        variant={props.variant ?? 'primary'}
        disabled={props.busy === true || reading}
        onClick={() => fileRef.current?.click()}
        data-testid={props.testId ?? 'evidence-capture-take'}
      >
        {reading ? 'Reading…' : props.label ?? 'Take photo'}
      </AcButton>
      <input
        ref={fileRef}
        type="file"
        disabled={props.busy === true || reading}
        accept={accept}
        {...(cameraEnabled ? { capture: 'environment' as const } : {})}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.[0]) props.onFileSelected?.();
          void onPick(e.target.files?.[0] ?? null);
          // Allow re-picking the same file (retake of an identical shot).
          e.target.value = '';
        }}
        data-testid={
          props.testId ? `${props.testId}-input` : 'evidence-capture-input'
        }
      />
    </span>
  );
}
